import {Build, BuildTrackChoices, BuildPlatformChoices} from "../../../models/build";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import DownloadIcon from '@mui/icons-material/Download';
import Link from "@mui/material/Link";
import ArticleIcon from '@mui/icons-material/Article';
import {useNavigate} from "react-router-dom";

interface PreviewBuildProps {
    build: Build;
}

export default function PreviewBuild({build}: PreviewBuildProps) {
    const navigate = useNavigate();

    const getDocumentationLink = (platform?: string): string | null => {
        if (!platform) return null;
        
        const platformLower = platform.toLowerCase();
        
        // Map platform values to documentation hash anchors
        // 1. web or docker -> docker documentation
        if (platformLower === 'web' || platformLower === 'docker') {
            return '/documentation#docker';
        }
        
        // 2. windows -> windows documentation
        if (platformLower === 'windows') {
            return '/documentation#windows';
        }
        
        // 3. macos -> macos documentation
        if (platformLower === 'macos') {
            return '/documentation#macos';
        }
        
        // 4. android or ios -> no documentation link
        if (platformLower === 'android' || platformLower === 'ios') {
            return null;
        }
        
        // For any other platform, return null
        return null;
    };

    const handleDocumentationClick = () => {
        const docLink = getDocumentationLink(build.platform);
        if (docLink) {
            navigate(docLink);
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateString;
        }
    };

    const formatPlatform = (platform?: string) => {
        if (!platform) return 'N/A';
        const platformOption = BuildPlatformChoices.find(p => p.value === platform);
        return platformOption ? platformOption.label : platform.split('_').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    };

    const formatTrack = (track?: string) => {
        if (!track) return 'N/A';
        const trackOption = BuildTrackChoices.find(t => t.value === track);
        return trackOption ? trackOption.label : track.split('_').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    };

    const formatDeploymentType = (deploymentType?: string) => {
        if (!deploymentType) return 'N/A';
        return deploymentType.split('_').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    };

    const getTrackColor = (track?: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
        switch (track) {
            case 'stable':
                return 'success';
            case 'beta':
                return 'warning';
            case 'dev':
                return 'info';
            default:
                return 'default';
        }
    };

    return (
        <Container 
            id="build-preview"
            sx={{
                pt: { xs: 4, sm: 6 },
                pb: { xs: 4, sm: 6 },
                px: { xs: 3, sm: 4 },
                maxWidth: '800px',
                margin: '0 auto'
            }}
        >
            {/* Header Section */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Typography 
                    component="h1" 
                    variant="h3" 
                    gutterBottom 
                    sx={{ 
                        color: 'text.primary',
                        fontWeight: 600,
                        mb: 2
                    }}
                >
                    {build.version_number ? `v${build.version_number}` : 'Build Details'}
                </Typography>

                <Divider sx={{ my: 3 }} />

                {/* Metadata */}
                <Box 
                    sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        gap: 3,
                        flexWrap: 'wrap',
                        mb: 2
                    }}
                >
                    <Chip 
                        label={formatTrack(build.track)} 
                        color={getTrackColor(build.track)}
                        sx={{ fontWeight: 600 }}
                    />
                    <Chip 
                        label={formatPlatform(build.platform)} 
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                        <strong>Released:</strong> {formatDate(build.released_at)}
                    </Typography>
                    {build.deployment_type && (
                        <Typography variant="body2" color="text.secondary">
                            <strong>Deployment:</strong> {formatDeploymentType(build.deployment_type)}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Download Section */}
            {(build.release_link || getDocumentationLink(build.platform)) && (
                <Box 
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: 2,
                        p: { xs: 3, sm: 4 },
                        border: '1px solid',
                        borderColor: 'divider',
                        mb: 3,
                        textAlign: 'center'
                    }}
                >
                    {build.release_link && (
                        <>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<DownloadIcon />}
                                component={Link}
                                href={build.release_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ mb: 2 }}
                            >
                                Download Build
                            </Button>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Click the button above to download this build
                            </Typography>
                        </>
                    )}
                    {getDocumentationLink(build.platform) && (
                        <Button
                            variant={build.release_link ? "outlined" : "contained"}
                            size={build.release_link ? "medium" : "large"}
                            startIcon={<ArticleIcon />}
                            onClick={handleDocumentationClick}
                            sx={{ mt: build.release_link ? 1 : 0 }}
                        >
                            View Installation Documentation
                        </Button>
                    )}
                </Box>
            )}

            {/* Release Notes Section */}
            {build.release_notes && (
                <Box 
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: 2,
                        p: { xs: 3, sm: 4 },
                        border: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <Typography 
                        variant="h6" 
                        gutterBottom 
                        sx={{ 
                            color: 'text.primary',
                            fontWeight: 600,
                            mb: 2
                        }}
                    >
                        Release Notes
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    <Typography 
                        variant="body1" 
                        sx={{ 
                            color: 'text.primary',
                            lineHeight: 1.7,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                        }}
                    >
                        {build.release_notes}
                    </Typography>
                </Box>
            )}

            {/* Additional Details */}
            <Box 
                sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: 2,
                    p: { xs: 3, sm: 4 },
                    border: '1px solid',
                    borderColor: 'divider',
                    mt: 3
                }}
            >
                <Typography 
                    variant="h6" 
                    gutterBottom 
                    sx={{ 
                        color: 'text.primary',
                        fontWeight: 600,
                        mb: 2
                    }}
                >
                    Build Information
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {build.version_number && (
                        <Typography variant="body2" color="text.secondary">
                            <strong>Version:</strong> {build.version_number}
                        </Typography>
                    )}
                    {build.platform && (
                        <Typography variant="body2" color="text.secondary">
                            <strong>Platform:</strong> {formatPlatform(build.platform)}
                        </Typography>
                    )}
                    {build.track && (
                        <Typography variant="body2" color="text.secondary">
                            <strong>Track:</strong> {formatTrack(build.track)}
                        </Typography>
                    )}
                    {build.deployment_type && (
                        <Typography variant="body2" color="text.secondary">
                            <strong>Deployment Type:</strong> {formatDeploymentType(build.deployment_type)}
                        </Typography>
                    )}
                    {build.released_at && (
                        <Typography variant="body2" color="text.secondary">
                            <strong>Released:</strong> {formatDate(build.released_at)}
                        </Typography>
                    )}
                    {build.created_at && (
                        <Typography variant="body2" color="text.secondary">
                            <strong>Created:</strong> {formatDate(build.created_at)}
                        </Typography>
                    )}
                    {build.updated_at && build.updated_at !== build.created_at && (
                        <Typography variant="body2" color="text.secondary">
                            <strong>Updated:</strong> {formatDate(build.updated_at)}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Container>
    );
}
